"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { Mail, Lock, Eye, EyeOff, X } from "lucide-react";

export default function LoginPage() {
  const { login, isLoading, isAuthenticated, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [selectedMode, setSelectedMode] = useState<
    "forward" | "reverse" | "marketplace"
  >("forward");
  const [hostname, setHostname] = useState("");
  const [isModeLocked, setIsModeLocked] = useState(false);

  // Close handler — goes back or to home
  const handleClose = useCallback(() => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      router.push(redirectParam);
    } else {
      router.back();
    }
  }, [router, searchParams]);

  // ESC key closes the login page
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  // Detect hostname on client side
  useEffect(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const search = window.location.search;
      setHostname(host);

      if (
        host === "forwardauction.briskon.com" ||
        host.includes("forwardauction") ||
        search.includes("mode=forward")
      ) {
        setSelectedMode("forward");
        setIsModeLocked(true);
      } else if (
        host === "reverseauction.briskon.com" ||
        host.includes("reverseauction") ||
        search.includes("mode=reverse")
      ) {
        setSelectedMode("reverse");
        setIsModeLocked(true);
      } else if (
        host === "auction.briskon.com" ||
        search.includes("mode=marketplace")
      ) {
        setSelectedMode("marketplace");
        setIsModeLocked(true);
      }
    }
  }, []);

  // Determine logo based on hostname or selected mode
  const getLogoSrc = () => {
    // Check if hostname is forward auction domain (with variations)
    if (
      hostname === "briskon-forward-auction.vercel.app" ||
      hostname === "www.briskon-forward-auction.vercel.app" ||
      hostname.includes("briskon-forward-auction")
    ) {
      console.log(
        "Login page - Using forward auction logo for hostname:",
        hostname,
      ); // Debug log
      return "/briskon-forward-auction-logo.svg";
    }

    // Use mode selection for logo switching
    if (selectedMode === "forward") {
      return "/briskon-forward-auction-logo.svg";
    }

    if (selectedMode === "reverse") {
      return "/briskon-reverse-auction-logo.svg";
    }

    if (selectedMode === "marketplace") {
      return "/briskon-auction-logo.svg";
    }

    return "/briskon-auction-logo.svg";
  }; //

  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectParam = searchParams.get("redirect");
      if (redirectParam) {
        router.push(redirectParam);
        return;
      }
      // Redirect based on user role
      switch (user.role) {
        case "buyer":
          router.push("/dashboard/buyer");
          break;
        case "seller":
          router.push("/dashboard/seller");
          break;
        case "both":
          router.push("/dashboard");
          break;
        default:
          router.push("/");
      }
    }
  }, [isAuthenticated, user, router, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const result = await login(email, password, selectedMode);

    if (!result.success) {
      setError(result.error || "Login failed");
    }
  };

  const loading = isLoading;
  const project = selectedMode;
  const setProject = (mode: "forward" | "reverse" | "marketplace") =>
    setSelectedMode(mode);
  const handleLogin = handleSubmit;

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center text-gray-900">
          <div className="w-8 h-8 border-2  border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="dark:text-white">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-4 sm:p-6 md:p-10 lg:p-14 2xl:p-20 bg-slate-950">
      {/* Global Backdrop Layer - Blurred background spanning the whole screen */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src="/ice.jpg"
          alt="App Background"
          className="w-full h-full object-cover opacity-60 grayscale-[0.1]"
        />
        <div className="absolute inset-0 bg-[#121951]/80" />
      </div>

      {/* Main Login Card - Centered Split Layout */}
      <div className="relative z-10 w-full max-w-[1440px] min-h-[500px] md:h-auto md:max-h-[900px] bg-transparent rounded-[2rem] shadow-[0_35px_60px_-15px_rgba(0,0,0,0.5)]
       flex flex-col md:flex-row overflow-hidden border border-white/10 animate-in fade-in zoom-in duration-500 my-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          aria-label="Close login"
          className="absolute top-4 right-4 z-50 flex items-center justify-center 
          w-9 h-9 rounded-full bg-white/40 hover:bg-white/20 text-black  transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-white/20"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Left Section - Image & Branding (Matching the reference image style) */}
        <div className="w-full md:w-1/2 relative min-h-[350px] md:min-h-[500px] h-auto overflow-hidden flex flex-col justify-end p-8 lg:p-12">
          {/* Inner Image Layer (Unblurred mountain) */}
          <div className="absolute inset-0 z-0">
            <img
              src="/ice.jpg"
              alt="Design Detail"
              className="w-full h-full object-cover"
            />
            {/* Dark Gradient Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 via-transparent to-transparent" />
          </div>

          {/* Branding Content pinned to bottom-left */}
          <div className="relative z-10 space-y-4">
            <h1 className="text-3xl md:text-4xl 2xl:text-5xl text-[#12195a] font-normal drop-shadow-2xl">
              A-Z AUCTION
            </h1>
            <div className="flex flex-wrap gap-2 pt-4">
              <div
                className={`px-4 py-[7px] rounded-full text-[11px] font-normal backdrop-blur-md border flex items-center gap-2 transition-all duration-300 ${
                  selectedMode === "forward"
                    ? "bg-[#313eba] text-white border-[#313eba]"
                    : "bg-white text-[#12195a] border-gray-500"
                }`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#12195a]" />{" "}
                FORWARD
              </div>

              <div
                className={`px-4 py-2 rounded-full text-[10px] font-normal backdrop-blur-md border flex items-center gap-2 transition-all duration-300 ${
                  selectedMode === "reverse"
                    ? "bg-[#313eba] text-white border-[#313eba]"
                    : "bg-white text-[#12195a] border-gray-500"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    selectedMode === "reverse" ? "bg-white" : "bg-[#12195a]"
                  }`}
                />{" "}
                REVERSE
              </div>
              <div
                className={`px-4 py-2 rounded-full text-[10px] font-normal backdrop-blur-md border flex items-center gap-2 transition-all duration-300 ${
                  selectedMode === "marketplace"
                    ? "bg-[#313eba] text-white border-[#313eba]"
                    : "bg-white text-[#12195a] border-gray-500"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    selectedMode === "marketplace" ? "bg-white" : "bg-[#12195a]"
                  }`}
                />{" "}
                MARKETPLACE
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Authentication Form */}
        <div className="flex-1 !bg-white p-6 md:p-10 lg:p-16 flex flex-col justify-start md:justify-center overflow-y-auto py-3 md:py-6 2xl:py-8">
          <div className="max-w-md mx-auto w-full space-y-3 2xl:space-y-6">
            {/* Header Area with Briskon Logo */}
            <div className="space-y-2 2xl:space-y-6">
              <div className="flex items-center gap-3">
                <Image
                  src={getLogoSrc()}
                  alt="Briskon Logo"
                  width={150}
                  height={44}
                  className="h-9 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm 2xl:text-base leading-relaxed font-medium">
                Enter your email and password to access your account.
              </p>
            </div>

            {/* Mode Selection Tabs (Unified with the form) */}
            {!isModeLocked && (
              <div className="flex gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100">
                <button
                  onClick={() => setProject("forward")}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                    project === "forward"
                      ? "bg-white shadow-[0_2px_8px_rgba(37,99,235,0.1)] text-[#313eba]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Forward
                </button>
                <button
                  onClick={() => setProject("reverse")}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                    project === "reverse"
                      ? "bg-white shadow-[0_2px_8px_rgba(37,99,235,0.1)] text-[#313eba]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Reverse
                </button>
                <button
                  onClick={() => setProject("marketplace")}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                    project === "marketplace"
                      ? "bg-white shadow-[0_2px_8px_rgba(37,99,235,0.1)] text-[#313eba]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Marketplace
                </button>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-3 2xl:space-y-6">
              <div className="space-y-3 2xl:space-y-5">
                {/* Email address field */}
                <div className="space-y-1 2xl:space-y-2">
                  <label className="text-xs 2xl:text-sm font-medium !text-black ml-1">
                    Email address
                  </label>
                  <div className="group relative">
                    {/* Black outline */}
                    <div className="pointer-events-none absolute inset-0 rounded-[2rem] border !border-black" />
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 transition-colors" />
                    <input 
                      type="email"
                      required
                      className="
        w-full pl-12 pr-4 py-2 2xl:py-4
        !bg-white !text-black caret-black
        rounded-[2rem]
        outline-none
        appearance-none
        border-none
        focus:outline-none
        text-sm font-medium
      "
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                {/* Password field */}
                <div className="space-y-1 2xl:space-y-2 pb-2">
                  <label className="text-xs 2xl:text-sm font-medium !text-black ml-1">
                    Password
                  </label>
                  <div className="group relative">
                    {/* Black outline */}
                    <div className="pointer-events-none absolute inset-0 rounded-[2rem] border !border-black" />
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 transition-colors" />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      className=" w-full pl-12 pr-4 py-2 2xl:py-4
        !bg-white !text-black 
        rounded-[2rem]
        outline-none
        appearance-none
        border-none
        focus:outline-none
        text-sm font-medium"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               
                    </button>
                  </div>
                </div>
              </div> 
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-xs 
                font-bold rounded-2xl animate-in fade-in slide-in-from-top-1 duration-300">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#313eba] hover:bg-[#313eba]/80 text-white font-normal py-3 2xl:py-4 
                rounded-[2rem] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl
                 shadow-blue-500/20 active:scale-[0.98]  text-base 2xl:text-[19px]"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sign in
                  </>
                ) : (
                  "Sign in"
                )}
              </button>

              <div className="flex items-center justify-between mx-1">
                <label className="flex items-center gap-2 cursor-pointer g`roup">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all"
                  />
                  <span className="text-[11px] 2xl:text-xs font-normal !text-black/60 group-hover:!text-black/60 transition-colors">
                    Remember me
                  </span>
                </label>
                <a
                  href="#"
                  className="text-[11px] 2xl:text-[12px] font-medium !text-gray-600 hover:!text-[#313eba]/80 "
                >
                  Forgot password?
                </a>
              </div>
            </form>

            <div className="pt-3 2xl:pt-8 text-left text-[11px] font-medium !text-black/60">
              © 2026 Briskon ·{" "}
              <a
                href="#"
                className="hover:text-gray-600 transition-colors underline  underline-offset-2"
              >
                Privacy policy
              </a>{" "}
              ·{" "}
              <a
                href="#"
                className="hover:text-gray-600 transition-colors underline  underline-offset-2"
              >
                Terms and conditions
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
